<!--
  A Bulma-styled date input field with a calendar widget, validation, and two-way binding for the input value.
  Value is expected to be a unix timestamp (number)
-->
<template>
  <div class="fieldWrapper field">
    <div style="display:flex">
      <label v-if="label" class="label">{{ label }}</label>
      <slot name="top"/>
    </div>
    <div class="control has-icons-left">
      <input
        ref="inputElem"
        class="input"
        type="text"
        :disabled="isDisabled"
        :placeholder="placeholder"
        :class="{ 'is-danger': validator ? !validator(validationId ? validationId : this, modelValue) : false }"
      />
      <span class="icon is-small is-left">
        <i class="fas fa-calendar"></i>
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from "vue";

import { timestampToYYYYMMDD, YYYYMMDDToTimestamp } from "@/core/utils"

import 'thedatepicker'; // Types
require('thedatepicker/dist/the-datepicker.js'); // Implementation

const props = defineProps<{
  modelValue?: number|string|Date,
  label?: string,
  validator?: (fieldId: any, val: any) => boolean,
  validationId?: string,
  placeholder?: string,
  isDisabled?: boolean,
  past?: boolean,
  future?: boolean,
}>();

// The input element where the form field's value will be bound
const inputElem = ref<any>(null);

// Event for two-way binding
const emit = defineEmits(['update:modelValue'])

const utcToLocal = (d: Date) => {
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

const localToUtc = (d: Date) => {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
}

// Enable calendar
onMounted(()=>{
  const datepicker = new (window as any).TheDatepicker.Datepicker(inputElem.value) as TheDatepicker.Datepicker;

  datepicker.options.setInputFormat("m/d/Y");

  const today = utcToLocal(new Date());

  if (props.past) {
    datepicker.options.setMaxDate(today);
  } else if (props.future) {
    datepicker.options.setMinDate(today);
  }

  // Set initial value
  if (props.modelValue) {
    datepicker.selectDate(utcToLocal(new Date(props.modelValue)));
  }

  // Emit on date change
  datepicker.options.onSelect((event,day,prevDay) => {
    if (!day) {
      emit("update:modelValue", null);
      return true;
    }

    // Truncate to day, and convert to UTC
    const d = localToUtc(day.getDate());

    // Preserve the original type of the bound value
    if (!props.modelValue || props.modelValue instanceof Date) {
      emit("update:modelValue", d);
    }
    else if (typeof props.modelValue == "number") {
      // Unix timestamp
      emit("update:modelValue", d.getTime());
    }
    else if (typeof props.modelValue == "string") {
      emit("update:modelValue", timestampToYYYYMMDD(d.getTime()));
    }

    return true;
  });

  datepicker.render();
});

</script>

<style lang="scss">
@import '~thedatepicker/dist/the-datepicker.css';
@import '../assets/variables.scss';

// Note - using 'important!' all over the place here, as Bulma modals mess with these, overriding colours, and make it unusable otherwise
.the-datepicker__main {
  font-family: $primaryFont !important;
}

.the-datepicker__main select.the-datepicker__select {
  font-family: $primaryFont !important;
  padding: 0.3rem 0.2rem 0.3rem 0.4rem !important;
}

.the-datepicker__deselect-button {
  left: -1.5rem !important;
  font-size: 25px !important;
  color: gray !important;
}

.the-datepicker__close a.the-datepicker__button {
  font-size: 150% !important;
  padding: 0.5rem 0 !important;
  width: 2rem !important;
}

.the-datepicker__main a.the-datepicker__button {
  color: $primaryMid !important;
  text-decoration: none !important;
}

.the-datepicker__main td.the-datepicker__cell--unavailable a.the-datepicker__button {
  color: #aaa !important;
}

.the-datepicker__main td.the-datepicker__cell--selected a.the-datepicker__button, .the-datepicker__main td.the-datepicker__cell--selected.the-datepicker__cell--highlighted a.the-datepicker__button {
  color: #fff !important;
}

.the-datepicker__main td.the-datepicker__cell--selected a.the-datepicker__button .the-datepicker__button-content,
.the-datepicker__main td.the-datepicker__cell--selected.the-datepicker__cell--highlighted a.the-datepicker__button .the-datepicker__button-content {
  background-color: $primaryMid !important;
}
.the-datepicker__main td.the-datepicker__cell--selected a.the-datepicker__button:hover .the-datepicker__button-content,
.the-datepicker__main td.the-datepicker__cell--selected.the-datepicker__cell--highlighted a.the-datepicker__button:hover .the-datepicker__button-content {
  background-color: $primaryMid !important;
}
.the-datepicker__main--darkmode.the-datepicker__main td.the-datepicker__cell--selected a.the-datepicker__button:hover .the-datepicker__button-content,
.the-datepicker__main--darkmode.the-datepicker__main td.the-datepicker__cell--selected.the-datepicker__cell--highlighted a.the-datepicker__button:hover .the-datepicker__button-content {
  background-color: $primaryMid !important;
}
.the-datepicker__deselect .the-datepicker__deselect-button {
  color: $primaryMid !important;
}
</style>